/* eslint-disable react-hooks/exhaustive-deps */

import { Box, ThemeProvider, createTheme } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { showToast } from 'modules/layout/layout.actions';

import {
  getAuthClientPolicies,
  getAuthDateSummary,
  getAuthorizationSummary,
  getCommunicationInfo,
  getDepartureAddress,
  getDestinationAddress,
  getEmployeeInfo,
  getFamilyMembers,
  getFamilyPets,
  getServices,
  getTeam,
  getUserSettings,
  setAuthId,
  setLoadingComplete
} from '../../store/actions/authorizations.actions';

import {
  getBrokerAgents,
  getExpenseCodesByClientId,
  getInspectors,
  getVendorContacts,
  getVendors
} from 'modules/authorizations/store/actions/dataManagement.actions';

import {
  getAllAppraisals,
  getAllListings,
  getHomesaleInfo,
  getHomesaleResaleOffers,
  getInspections,
  getBuyout
} from 'modules/authorizations/store/actions/homesale.actions';
import { getTitleReps } from 'modules/authorizations/store/actions/dataManagement.actions';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

import HomesaleService from './homesaleService.component';
import {
  fetchAuthDetails,
  fetchAuthHomesaleDetails,
  fetchBrokerAgents,
  fetchEnumerations,
  fetchInspectors,
  fetchVendors
} from 'modules/authorizations/utils/fetchDataDetailsService';

import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';
import { getAgents } from 'modules/common/common.actions';
import { getEmailTemplateSummaries } from 'modules/emailTemplates/store/emailTemplate.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';

const styles = theme => ({
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  }
});
const SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID = '83BBE794-E171-403E-B545-23508B017354';

const HomesaleView = props => {
  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });
  const details = props.authorizationDetails ? props.authorizationDetails[props.match.params.id] : null;

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const id = props.match.params.id;

      window.scrollTo(0, 0);
      props.setAuthId(id);
      setIsLoading(true);

      const fetchedAuthDetails = await fetchAuthDetails(details, id, props);
      await fetchVendors(props?.vendors, props);
      // await fetchVendorContacts(props?.vendorContacts, props);
      await fetchBrokerAgents(props?.brokerAgents, props);
      await fetchInspectors(props?.inspectors, props);
      await fetchAuthHomesaleDetails(details, id, props);
      await fetchEnumerations(props);
      if (!props.templates || props.templates.isLoaded === false) {
        await props.getEmailTemplateSummaries();
      }
      await props.getAgents();

      // Use the already-fetched auth summary to avoid a second getAuthorizationSummary call,
      // which would overwrite the Redux entry and wipe the homesale data just loaded above.
      const clientRecordId = details?.authorizationDetails?.clientId
        || fetchedAuthDetails?.authorizationDetails?.clientId;

      let expenseCodeClientId = null;
      if (clientRecordId) {
        props.setClientId(clientRecordId);
        const clientResp = await props.getClientDetails(clientRecordId);
        expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
      }

      if (expenseCodeClientId) {
        await props.getExpenseCodesByClientId(expenseCodeClientId);
      }

      setIsLoading(false);
    }

    fetchData();
  }, []);

  const canReadHomesale = hasPermission(Permissions.File.Homesale.Read);
  const canEditHomesale = hasPermission(Permissions.File.Homesale.Edit);
  const isShortTermAssignment = details?.authorizationDetails?.moveTypeId?.toUpperCase() === SHORT_TERM_ASSIGNMENT_MOVE_TYPE_ID;
  const staEditAccess = hasPermission(Permissions.File.STA.Edit);

  if (!canReadHomesale) {
    props.history.push('/');
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading &&
          details &&
          details.homesale &&
          props.clientsInfo &&
          props.enumerations.allEnums &&
          props.agents &&
          props.expenseCodes && (
            <HomesaleService
              brokerAgents={props.agents || []}
              details={details}
              clientInfo={props.clientsInfo[details.authorizationDetails.clientId]}
              id={props.match.params.id}
              history={props.history}
              location={props.location}
              vendors={props.vendors}
              inspectors={props.inspectors}
              titleReps={props.titleReps}
              isReadOnly={isShortTermAssignment ? !staEditAccess : !canEditHomesale}
              enumerations={props.enumerations}
              emailTemplates={props.templates ? props.templates.emailTemplates : []}
            />
          )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

HomesaleView.propTypes = {
  classes: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const {
    authorizationDetails,
    brokerAgents,
    vendors,
    inspectors,
    titleReps,
    vendorContacts,
    expenseCodes
  } = state.authorizations;
  const { unmappedClients, clientsInfo } = state.clients;
  const enumerations = state.enumerations;
  const { agents } = state.common;
  const templates = state.templates;
  return {
    authorizationDetails,
    brokerAgents,
    unmappedClients,
    inspectors,
    vendors,
    titleReps,
    vendorContacts,
    enumerations,
    agents,
    expenseCodes,
    templates,
    clientsInfo
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getAuthorizationSummary,
    getEmployeeInfo,
    getCommunicationInfo,
    getAuthClientPolicies,
    setAuthId,
    setLoadingComplete,
    getAuthDateSummary,
    getDepartureAddress,
    getDestinationAddress,
    getTeam,
    getServices,
    getUserSettings,
    getVendorContacts,
    getVendors,
    getHomesaleInfo,
    getBrokerAgents,
    getAllAppraisals,
    showToast,
    getInspectors,
    getInspections,
    getAllListings,
    getTitleReps,
    getHomesaleResaleOffers,
    getExpenseCodesByClientId,
    getFamilyMembers,
    getFamilyPets,
    getAllEnumerations,
    getAllSaleTypes,
    getAllWeightTypes,
    getAllProvinces,
    getAllCurrencies,
    getAllCountries,
    getAgents,
    getBuyout,
    getEmailTemplateSummaries,
    getClientDetails,
    setClientId
  })
)(HomesaleView);
